import type { AccommodationVariant, Product as ApiProduct, Service } from '@/types/schemas';
import type { Product as UiProduct } from '@/types/Product';
import type { NBCProductListItem } from '@/services/api/nbc';
import { getFirstTeaserImage } from '@/utils/nbc/nbcHelpers';
import { $t } from '@/composables/translations/useTranslate';
import { daysBetween } from '@/utils/date/saturdayWindow';
import { normalizeToArray } from '@/services/mappers/arrayNormalizers';
import { boardLabel } from '@/utils/formatting/boardLabel';

function findAccommodationVariant(
  service: Service | undefined,
  serviceVariantId: number | undefined
): AccommodationVariant | undefined {
  if (!service || serviceVariantId === undefined) {
    return undefined;
  }

  const variants = normalizeToArray(
    service.AccommodationService?.AccommodationVariants?.AccommodationVariant
  );

  return variants.find((variant) => variant.Id === serviceVariantId);
}

function nightsInVariant(variant: AccommodationVariant | undefined): number | undefined {
  if (!variant?.Start || !variant?.End) {
    return undefined;
  }

  const nights = daysBetween(new Date(variant.Start), new Date(variant.End));

  return Number.isFinite(nights) && nights > 0 ? nights : undefined;
}

/**
 * Map API Product to UI Product format
 *
 * Combines data from Player API and optionally NBC API for enhanced product cards.
 *
 * @param apiProduct - Product from Player API
 * @param service - Matching AccommodationService from API response
 * @param nbcData - Optional NBC teaser data for enrichment
 * @param productCode - Optional product code
 * @param variant - Accommodation variant the displayed price belongs to; supplies
 *   the offer's own dates and length, which need not match the selected duration,
 *   and its included board, appended to the price description ("…, Halbpension")
 * @returns UI Product with NBC enrichment when available
 */
export function mapApiProductToUiProduct(
  apiProduct: ApiProduct,
  service: Service | undefined,
  nbcData?: NBCProductListItem,
  productCode?: string,
  variant?: AccommodationVariant
): UiProduct {
  const accommodation =
    service?.AccommodationService?.AccommodationSpecifics?.AccommodationSpecific?.[0];
  const location = service?.AccommodationService?.Location;

  const hotelName = accommodation?.Name || $t('ibe.product.unknown-hotel', 'Unknown Hotel');

  let locationString = '';
  if (location) {
    const parts = [];
    if (location.Country?.Name) parts.push(location.Country.Name);
    if (location.Region?.Name) parts.push(location.Region.Name);
    locationString = parts.join(' / ');
  }

  const priceInfo = apiProduct.PriceInformation;
  const totalPrice = priceInfo?.Amount || 0;

  const priceTypes = priceInfo?.PriceTypes?.PriceType || [];
  const hasDiscount = priceTypes.length > 0;
  const discountType = hasDiscount ? priceTypes[0] : undefined;

  const priceDetails = priceInfo?.PriceDetails?.PriceDetail || [];
  let oldPrice: number | undefined;
  let discountPercentage: number | undefined;

  if (priceDetails.length > 0) {
    const totalDiscount = priceDetails.reduce((sum, detail) => sum + detail.Amount, 0);

    oldPrice = totalPrice - totalDiscount;

    if (oldPrice > 0) {
      discountPercentage = Math.round((Math.abs(totalDiscount) / oldPrice) * 100);
    }
  }

  const offerDuration = nightsInVariant(variant);
  const board = boardLabel(normalizeToArray(variant?.Boards?.Board)[0]?.Name);
  const durationText = offerDuration
    ? $t('ibe.product.price-for-duration', 'Family price for {n} days', { n: offerDuration })
    : $t('ibe.product.price-per-week-in', 'Family price for selected duration');

  const nbcImage = nbcData ? getFirstTeaserImage(nbcData) : undefined;

  const images = nbcImage
    ? nbcData!.teaserImages.map((url) => ({
        imgPath: url,
        imgExt: 'jpg',
        imgAlt: nbcData!.teaserHeadline || hotelName,
      }))
    : [
        {
          imgPath: `https://placehold.co/574x742/EBE7DB/6C6B3C?text=${encodeURIComponent(hotelName)}`,
          imgExt: 'jpg',
          imgAlt: hotelName,
        },
      ];

  const tags =
    nbcData?.tags.map((tag) => ({
      text: tag.label,
    })) || [];

  return {
    id: apiProduct.Id,
    slug: nbcData?.pdpUrl || '/',
    productCode,
    title: nbcData?.teaserHeadline || hotelName,
    location: locationString || nbcData?.destinationName || '',
    price: totalPrice,
    priceDescription: [durationText, board].filter(Boolean).join(', '),
    discount: discountPercentage,
    discountReason: discountType,
    oldPrice,
    images,
    tags,
    description: nbcData?.teaserText || '',
    offerStartDate: variant?.Start,
    offerEndDate: variant?.End,
    offerDuration,
  };
}

/**
 * Map array of API products to UI products
 *
 * Combines Player API products with optional NBC enrichment data.
 *
 * @param apiProducts - Array of products from Player API
 * @param services - Array of services from Player API
 * @param nbcDataMap - Optional Map of NBC data by product code
 * @returns Array of UI Products with NBC enrichment when available
 */
export function mapApiProductsToUiProducts(
  apiProducts: ApiProduct[],
  services: Service[],
  nbcDataMap?: Map<string, NBCProductListItem>
): UiProduct[] {
  return apiProducts.map((product) => {
    const serviceRefs = normalizeToArray(product.ServiceReferences?.ServiceReference);
    const serviceId = serviceRefs[0]?.ServiceId;
    const matchingService = services.find((s) => s.Id === serviceId);

    const accommodation =
      matchingService?.AccommodationService?.AccommodationSpecifics?.AccommodationSpecific?.[0];
    const productCode = accommodation?.ProductCode;

    const nbcData = productCode && nbcDataMap ? nbcDataMap.get(productCode) : undefined;

    const variant = findAccommodationVariant(matchingService, serviceRefs[0]?.ServiceVariantId);

    return mapApiProductToUiProduct(product, matchingService, nbcData, productCode, variant);
  });
}
